import { QueryClient, useQuery, UseQueryOptions } from "@tanstack/react-query";
import jwtDecode, { JwtPayload } from "jwt-decode";

import { ResponseError } from "lib/generated/app-api";
import { useOpenApi } from "lib/openApiContext";

/** Cache key for the Rails-minted app-api token, shared by everything that reads or re-mints it. */
export const APP_TOKEN_QUERY_KEY = ["appToken"];

const TOKEN_EXPIRY_SECONDS = 600; // 10 minutes
const LEEWAY_SECONDS = 30; // amount of time we want to leave before we refetch the token
const STALE_TIME_SECONDS = TOKEN_EXPIRY_SECONDS - LEEWAY_SECONDS; // time after which the token is considered stale
const STALE_TIME_MS = STALE_TIME_SECONDS * 1000;

export function isTokenExpired(token: string) {
  const decoded: JwtPayload = jwtDecode(token);
  // The jwt token exp is stated in seconds since epoch
  const expiryDate = decoded.exp || 0;
  // We convert the current time in milliseconds since epock to seconds since epoch
  const currentTime = Date.now() / 1000;
  // Treat the token as "expired" slightly before actual expiry to give ourselves
  // time to refetch (and avoid connecting subscriptions with a near-expiry token).
  return currentTime >= expiryDate - LEEWAY_SECONDS;
}

/**
 * Returns a usable token, re-minting first if the one we hold has expired. React Query refreshes
 * the token on a timer, but a tab that was asleep can wake and fire a request in the same tick as
 * that refresh. Only the GraphQL layer recovers a rejected token downstream, so callers that
 * resolve their own authorization header check here instead. Concurrent callers share one refetch.
 */
export async function freshToken(
  queryClient: QueryClient | undefined,
  token: string,
): Promise<string> {
  if (!token || !queryClient || !isTokenExpired(token)) {
    return token;
  }
  await queryClient.refetchQueries({ queryKey: APP_TOKEN_QUERY_KEY });
  return queryClient.getQueryData<string>(APP_TOKEN_QUERY_KEY) ?? token;
}

export function useAppToken(options?: UseQueryOptions<string, unknown, string, string[]>) {
  const api = useOpenApi();

  const query = useQuery(
    APP_TOKEN_QUERY_KEY,
    async () => {
      // TODO: temporary workaround until auth moves to better-auth. The token endpoint is behind
      // Devise's authenticate_user!, which redirects unauthenticated browser requests to an HTML
      // login page (not a 401), so fetching it as a guest throws a JSON parse error. We skip the
      // fetch only when the server explicitly tells us the session is logged out. Checking for
      // `=== false` (not falsiness) is deliberate: the flag is injected by shared/_javascript_data
      // (the main app layout), but other React entry points like the Trestle admin pack don't
      // render it, leaving it `undefined` — there the user IS authenticated and we must still fetch
      // the token. Remove this guard once we move to better-auth.
      if (window.userSignedIn === false) {
        return "";
      }
      // The app token endpoint requires an authenticated session. Guests (public report / lead
      // URLs that don't require auth) get a 401, which we treat as "no token" so the app still
      // renders in guest mode rather than erroring. Other failures propagate to the error boundary.
      try {
        const data = await api.getAppApiToken();
        return data.token;
      } catch (e) {
        if (
          e instanceof ResponseError &&
          (e.response.status === 401 || e.response.status === 403)
        ) {
          return "";
        }
        throw e;
      }
    },
    {
      ...options,
      suspense: true, // means that loading state is handled by React Suspense and error state in error boundaries
      // `suspense` turns error-boundary throwing on for every error, including a failed *refresh* —
      // which surfaces on the next render of this subtree, so a 503 from the token endpoint replaces
      // the whole app at an arbitrary later moment. Only a user who never got a token is actually
      // stuck, so limit it to that: if we already hold one, keep serving it, and the requests that
      // start failing once it expires re-mint it on demand (see useGraphQlClient).
      useErrorBoundary: (_error, query) => !query.state.data,
      staleTime: STALE_TIME_MS,
      cacheTime: TOKEN_EXPIRY_SECONDS * 1000,
      refetchInterval: STALE_TIME_MS, // makes the query refetch the token when it turns stale
      // query-core skips interval ticks while the tab is hidden, so without this the token dies
      // there — and on return `activeUploads` refetches on focus in the same tick as this query,
      // sending the dead token before the refresh lands.
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true, // Only refetches if stale time is exceeded
    },
  );

  return query;
}
